import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  RdsAutocompleteComponent,
  RdsFormFieldComponent,
  RdsLabelDirective,
} from '@rds/angular-components';
import { map } from 'rxjs';

import { AuthService } from '../../../auth';
import { UserResult } from '../../model/user-management.model';
import { UserManagementService } from '../../services/user-management.service';

@Component({
  selector: 'bsh-user-search-autocomplete',
  imports: [
    RdsAutocompleteComponent,
    ReactiveFormsModule,
    FormsModule,
    RdsFormFieldComponent,
    RdsLabelDirective,
  ],
  providers: [
    UserManagementService,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UserSearchAutocompleteComponent,
    },
  ],
  templateUrl: './user-search-autocomplete.component.html',
  styleUrl: './user-search-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchAutocompleteComponent
  implements ControlValueAccessor, OnDestroy
{
  private static nextPanelId = 0;
  private static readonly panelViewportMarginPx = 16;
  private static readonly panelDesiredHeightPx = 190;

  readonly #userManagementService = inject(UserManagementService);
  readonly #authService = inject(AuthService);
  readonly #document = inject(DOCUMENT);
  readonly #hostElement = inject(ElementRef<HTMLElement>);
  @ViewChild(RdsAutocompleteComponent)
  private autocomplete?: RdsAutocompleteComponent<UserResult>;
  private removeWheelListener?: () => void;

  protected readonly optionsContainerClass = `bsh-user-search-autocomplete-panel-${UserSearchAutocompleteComponent.nextPanelId++}`;
  protected data = (query: string) => {
    return this.#userManagementService.loadUsers(query).pipe(
      map(users => {
        if (this.excludeCurrentUser) {
          const currentEmail = this.#authService.user?.email || '';
          if (currentEmail) {
            return users.filter(u => u.email !== currentEmail);
          }
        }
        return users;
      })
    );
  };

  @Input() config: { placeholder: string; label: string } = {
    placeholder: 'Search User...',
    label: 'Email',
  };

  @Input() minSearchChars: number = 0;

  @Input() excludeCurrentUser: boolean = false;

  protected user: UserResult | null = null;
  protected disabled = false;

  protected displayProperty(i: UserResult): string {
    return `${i.email}${i.fullName ? ` - (${i.fullName})` : ``}`;
  }
  protected uniqProperty(i: UserResult): string {
    return i.email;
  }

  protected propagateTouched() {}

  // eslint-disable-next-line
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  // eslint-disable-next-line
  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: string): void {
    if (value == null) {
      this.user = null;
      return;
    }
    this.user = {
      email: value,
      rocheLoginId: '',
      photo: '',
      fullName: '',
      givenName: '',
      familyName: '',
      rochePersistentId: '',
      costCenter: '',
      department: '',
    } as UserResult;
  }

  onInputChange(value: UserResult) {
    const email = value ? value.email : null;
    this.propagateChange(email);
    this.propagateTouched();
  }

  protected onDropdownShown() {
    if (typeof window === 'undefined') {
      return;
    }

    this.attachWheelListener();

    requestAnimationFrame(() => {
      this.adjustDropdownHeightToViewport();
    });
  }

  protected onDropdownHidden() {
    this.detachWheelListener();
  }

  ngOnDestroy(): void {
    this.detachWheelListener();
  }

  private adjustDropdownHeightToViewport() {
    const input = this.getInputElement();
    const panel = this.getPanelElement();

    if (!input || !panel) {
      return;
    }

    const inputRect = input.getBoundingClientRect();
    const panelWidth = this.getControlWidth(input);
    const availableBottomSpace =
      window.innerHeight -
      inputRect.bottom -
      UserSearchAutocompleteComponent.panelViewportMarginPx;
    const panelMaxHeight = Math.max(
      1,
      Math.min(
        UserSearchAutocompleteComponent.panelDesiredHeightPx,
        Math.floor(availableBottomSpace)
      )
    );

    panel.style.width = `${panelWidth}px`;
    panel.style.minWidth = `${panelWidth}px`;
    panel.style.maxWidth = `${panelWidth}px`;
    panel.style.maxHeight = `${panelMaxHeight}px`;
    panel.style.overflowY = 'auto';
    this.autocomplete?.updatePosition();
  }

  private attachWheelListener() {
    this.detachWheelListener();

    const listener = (event: WheelEvent) => {
      const panel = this.getPanelElement();

      if (!panel) {
        return;
      }

      const target = event.target as Node | null;
      if (target && panel.contains(target)) {
        return;
      }

      this.autocomplete?.hide();

      // Keep page scrolling natural when wheel happens outside the dropdown.
      if (typeof window !== 'undefined' && event.deltaY !== 0) {
        window.scrollBy({ top: event.deltaY, behavior: 'auto' });
      }
    };

    this.#document.addEventListener('wheel', listener, { capture: true });
    this.removeWheelListener = () => {
      this.#document.removeEventListener('wheel', listener, true);
    };
  }

  private detachWheelListener() {
    this.removeWheelListener?.();
    this.removeWheelListener = undefined;
  }

  private getPanelElement(): HTMLElement | null {
    return this.#document.querySelector(
      `.${this.optionsContainerClass}`
    ) as HTMLElement | null;
  }

  private getInputElement(): HTMLInputElement | null {
    return this.#hostElement.nativeElement.querySelector('input');
  }

  private getControlWidth(input: HTMLElement): number {
    const formFieldContainer = this.#hostElement.nativeElement.querySelector(
      '.rds-form-field__container'
    ) as HTMLElement | null;

    return Math.ceil(
      (formFieldContainer ?? input).getBoundingClientRect().width
    );
  }

  // eslint-disable-next-line
  private propagateChange(_value: string | null) {}
}
